import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import PeoplePicker, { Person } from '../PeoplePicker';
import BoardModal, { BMCancelButton, BMPrimaryButton, BMDangerButton } from '../BoardModal';
import {
  ProjectStakeholder, StakeholderSurfaces, STAKEHOLDER_SURFACE_DEFS,
  getProjectStakeholders, createProjectStakeholder, updateStakeholderSurfaces,
  deleteProjectStakeholder, surfacesOf,
} from '../../services/projectStakeholderService';
import {
  getProjectMembers, createProjectMember, ProjectMemberRole,
} from '../../services/projectMemberService';

// Project Settings → Stakeholder Access (PM-only). Add a person as a
// project Stakeholder and control which in-project tabs they see. Only the
// Stakeholder role is gated by these toggles; PM/TM/Observer are unaffected.

interface Props { pca: IPublicClientApplication; projectId: string; }

const allOn = (): StakeholderSurfaces => ({
  forms: true, tasks: true, decisions: true, issues: true, risks: true, milestones: true, documents: true,
});

export default function StakeholderAccessSection({ pca, projectId }: Props) {
  const [rows, setRows] = useState<ProjectStakeholder[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ProjectStakeholder | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try { setRows(await getProjectStakeholders(pca, projectId)); }
    catch (e) { setRows([]); setError(e instanceof Error ? e.message : 'Failed to load stakeholders'); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const toggle = async (row: ProjectStakeholder, field: keyof ProjectStakeholder) => {
    const nextRow = { ...row, [field]: !row[field] } as ProjectStakeholder;
    setRows(prev => (prev ?? []).map(r => r.dagrun_pm_projectstakeholderid === row.dagrun_pm_projectstakeholderid ? nextRow : r));
    try { await updateStakeholderSurfaces(pca, row.dagrun_pm_projectstakeholderid, surfacesOf(nextRow)); }
    catch { reload(); }
  };

  const doRemove = async () => {
    if (!removeTarget) return;
    try { await deleteProjectStakeholder(pca, removeTarget.dagrun_pm_projectstakeholderid); }
    catch { /* ignore */ }
    setRows(prev => (prev ?? []).filter(r => r.dagrun_pm_projectstakeholderid !== removeTarget.dagrun_pm_projectstakeholderid));
    setRemoveTarget(null);
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="eyebrow" style={{ margin: 0 }}>STAKEHOLDER ACCESS</div>
        <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>+ Add Stakeholder</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
        Stakeholders often aren't Team members, so control which in-project tabs each one sees. Toggles only
        hide tabs — they never grant access beyond the Stakeholder role. Overview is always visible. PM, Team
        Member, and Observer roles are unaffected.
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}

      {rows === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)', fontStyle: 'italic' }}>No stakeholders added to this project yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Stakeholder</th>
                {STAKEHOLDER_SURFACE_DEFS.map(d => <th key={d.key} style={{ ...th, textAlign: 'center' }}>{d.label}</th>)}
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.dagrun_pm_projectstakeholderid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                  <td style={td}>
                    <div style={{ fontSize: 13, color: 'var(--ink)' }}>{r.dagrun_pm_personname ?? '—'}</div>
                    {r.dagrun_pm_personemail && <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{r.dagrun_pm_personemail}</div>}
                  </td>
                  {STAKEHOLDER_SURFACE_DEFS.map(d => (
                    <td key={d.key} style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!r[d.field]} onChange={() => toggle(r, d.field)} />
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button type="button" onClick={() => setRemoveTarget(r)} aria-label="Remove stakeholder"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 15 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddStakeholderModal pca={pca} projectId={projectId}
          existingIds={new Set((rows ?? []).map(r => r.dagrun_pm_personid).filter(Boolean) as string[])}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); reload(); }} />
      )}

      {removeTarget && (
        <BoardModal open title="Remove stakeholder access" onClose={() => setRemoveTarget(null)}
          footer={<>
            <BMCancelButton onClick={() => setRemoveTarget(null)} />
            <BMDangerButton onClick={doRemove}>Remove</BMDangerButton>
          </>}>
          Remove <strong style={{ color: '#0F1117' }}>{removeTarget.dagrun_pm_personname}</strong>'s surface
          settings? Their project membership is not changed — only these visibility toggles are removed.
        </BoardModal>
      )}
    </div>
  );
}

function AddStakeholderModal({ pca, projectId, existingIds, onClose, onAdded }: {
  pca: IPublicClientApplication; projectId: string; existingIds: Set<string>;
  onClose: () => void; onAdded: () => void;
}) {
  const [person, setPerson] = useState<Person | null>(null);
  const [surfaces, setSurfaces] = useState<StakeholderSurfaces>(allOn());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!person || saving) return;
    if (existingIds.has(person.aadObjectId)) { setError('This person is already a stakeholder on this project.'); return; }
    setSaving(true); setError(null);
    try {
      // Register the person as a Stakeholder member if they aren't already a
      // member — the toggles only take effect for the Stakeholder role.
      const members = await getProjectMembers(pca, projectId).catch(() => []);
      const existing = members.find(m => m.dagrun_pm_userid === person.aadObjectId);
      if (!existing) {
        await createProjectMember(pca, {
          projectId, userId: person.aadObjectId, userName: person.displayName,
          role: ProjectMemberRole.Stakeholder, isExternal: true,
        });
      }
      await createProjectStakeholder(pca, {
        projectId, personId: person.aadObjectId, personName: person.displayName, surfaces,
      });
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add stakeholder.');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Stakeholder" width={480} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !person}>{saving ? 'Adding…' : 'Add Stakeholder'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>Person</label>
        <PeoplePicker pca={pca} value={person} onChange={setPerson} placeholder="Select a stakeholder…" />
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>Surfaces they can see (Overview is always visible):</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {STAKEHOLDER_SURFACE_DEFS.map(d => (
          <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={surfaces[d.key]} onChange={e => setSurfaces(s => ({ ...s, [d.key]: e.target.checked }))} />
            {d.label}
          </label>
        ))}
      </div>
      {error && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
    </BoardModal>
  );
}

const th: React.CSSProperties = { padding: '8px 8px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td: React.CSSProperties = { padding: '8px 8px', verticalAlign: 'middle' };
